import { useState } from 'react';
import "./css/homepage_help.css";
import "./css/privacy.css";
import logo from "./images/logo_ticket_buydeal.jpg";
import { useNavigate, useSearchParams } from 'react-router-dom';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState( searchParams.get('tab') === 'organisers' ? 'organisers' : 'buyers');

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'organisers' ? { tab: 'organisers' } : {});
  };
  

  const sections = {
    buyers: [
      {
        title: 'About these terms',
        content: (
          <>
            <p>
              These terms apply every time you buy a ticket through <strong>tickets.buydeal.eu</strong>,
              operated by Buydeal.eu B.V., Utrecht, Netherlands ("Buydeal", "we", "us").
              By completing a purchase you agree to these terms, so please read them before
              you check out.
            </p>
            <p>
              These terms are governed by  consumer protection rules and Dutch law. 
            </p>
          </>
        ),
      },
      {
        title: "What Buydeal does — and what we don't",
        content: (
          <>
            <p>
              Buydeal is a ticketing platform. We provide the technology that lets event
              organisers ("Organisers") sell tickets to you. When you buy a ticket, the
              contract for attending the event is between you and the Organiser — Buydeal
              facilitates the transaction but is not a party to it.
            </p>
            <p>
              This means the Organiser is responsible for the event itself: the programme,
              the venue, cancellations, and any changes. Buydeal is responsible for
              processing your payment, delivering your e-ticket, and supporting you with
              order-related queries.
            </p>
          </>
        ),
      },
      {
        title: 'Buying a ticket',
        content: (
          <>
            <p>
              When you complete payment, a binding purchase agreement is formed between you
              and the Organiser. You will receive your e-ticket by email shortly after
              payment is confirmed. Please make sure your email address is correct at
              checkout — we cannot be held responsible for tickets sent to a wrong address.
            </p>
            <p>
              Ticket prices are set by the Organiser. Buydeal may add a service fee per
              ticket; this will always be shown clearly before you confirm payment. Once a
              purchase is made it is final — see the Cancellations &amp; Refunds section below.
            </p>
            <p>
              You must be at least 18 years old, or have parental permission, to buy tickets
              for age-restricted events. The Organiser sets and enforces age requirements at
              the door.
            </p>
          </>
        ),
      },
      {
        title: 'Payment',
        content: (
          <>
            <p>
              Payments are processed securely by our payment provider (CM.com). We
              accept iDEAL, credit card, and other methods shown at checkout. Buydeal never
              stores your full card details.
            </p>
            <p>
              If your payment fails but an amount has been reserved on your account, it will
              be released automatically — the timeframe depends on your bank. If you believe
              you have been charged for a failed order, contact us at{' '}
              <a href="mailto:contact@buydeal.eu">contact@buydeal.eu</a>.
            </p>
          </>
        ),
      },
      {
        title: 'Receiving your ticket',
        content: (
          <>
            <p>
              Your e-ticket is delivered to the email address you provide at checkout.
              Check your spam or junk folder if it does not arrive within 15 minutes.
              Each ticket contains a unique QR code that is scanned at the event entrance.
            </p>
            <p>
              Keep your QR code private. Anyone in possession of your QR code may be able
              to use it for entry. Buydeal accepts no liability if your ticket is used by
              someone else because you shared or lost it.
            </p>
          </>
        ),
      },
      {
        title: 'Cancellations and refunds',
        content: (
          <>
            <p>
              Because e-tickets are delivered digitally and immediately, the 14-day
              right of withdrawal under Dutch consumer law (<em>Burgerlijk Wetboek</em> Art.
              6:230p) does not apply to ticket purchases. By completing your order you
              confirm that you waive this right.
            </p>
            <p>
              <strong>Event cancelled by the Organiser</strong> — if an event is cancelled,
              the Organiser is responsible for issuing refunds. Buydeal will facilitate the
              refund process where instructed by the Organiser. Service fees charged by
              Buydeal may will not be refunded .
            </p>
            <p>
              <strong>Event postponed or moved</strong> — if an event is postponed or relocated,
              the Organiser decides whether refunds are offered. Check the event page or
              contact the Organiser directly for their policy.
            </p>
            <p>
              <strong>Buyer cancellation</strong> — tickets are generally non-refundable once
              purchased unless the Organiser explicitly offers this. Check the event listing
              before buying.
            </p>
          </>
        ),
      },
      {
        title: 'Ticket transfers and resale',
        content: (
          <>
            <p>
              Whether you can transfer your ticket to someone else depends on the Organiser's
              policy for that event. This is shown on the event page. Reselling tickets for
              profit on secondary markets may be prohibited by the Organiser.
            </p>
            <p>
              Buydeal is not responsible for tickets bought or sold outside the official
              platform. Tickets obtained from unofficial sources may be invalid.
            </p>
          </>
        ),
      },
      {
        title: 'Your responsibilities',
        content: (
          <>
            <p>When using Buydeal you agree to:</p>
            <ul>
              <li>Provide accurate information at checkout</li>
              <li>Keep your QR code and account credentials secure</li>
              <li>Not attempt to purchase tickets using automated tools or bots</li>
              <li>Not use the platform for any unlawful purpose</li>
              <li>Comply with the Organiser's event rules and entry requirements</li>
            </ul>
            <p>
              We reserve the right to cancel orders and block access to the platform if
              we detect misuse, fraud, or repeated violations of these terms.
            </p>
          </>
        ),
      },
      {
        title: 'Our liability',
        content: (
          <>
            <p>
              Buydeal is responsible for the ticket sales service we provide. We are not
              responsible for the event itself, including but not limited to cancellations,
              changes to the programme or venue, or the conduct of the Organiser.
            </p>
            <p>
              Where we are liable for a direct loss caused by our service, our liability is
              limited to the value of the ticket(s) in the relevant order .
			   We are not liable for indirect or consequential losses such as travel
              costs, accommodation, or loss of income.
            </p>
            <p>
              Nothing in these terms limits liability for fraud, death, or personal injury
              caused by our negligence.
            </p>
          </>
        ),
      },
      {
        title: 'Intellectual property',
        content: (
          <p>
            All content on the Buydeal platform — including the website design, software,
            logos, and brand assets — belongs to Buydeal.eu B.V. You may
            not copy, reproduce, or distribute any part of the platform without our written
            permission. Your use of the platform is a limited, personal, non-transferable
            licence to buy and manage your tickets.
          </p>
        ),
      },
      {
        title: 'Platform availability',
        content: (
          <p>
            We aim to keep Buydeal available 24/7 but cannot guarantee uninterrupted access.
            The platform may be temporarily unavailable for maintenance or due to issues
            outside our control. We will aim to schedule planned maintenance outside peak
            hours and communicate disruptions in advance where possible.
          </p>
        ),
      },
      {
        title: 'Privacy',
        content: (
          <p>
            Your personal data is handled in accordance with our{' '}
            <a href="/privacy">Privacy &amp; Cookie Policy</a>. By using Buydeal you agree
            to that policy.
          </p>
        ),
      },
      {
        title: 'Changes to these terms',
        content: (
          <p>
            We may update these terms from time to time. The version published on this page
            is always the current one. For significant changes we will notify registered
            users by email. Continued use of the platform after changes are published
            constitutes acceptance of the updated terms.
          </p>
        ),
      },
      {
        title: 'Applicable law and disputes',
        content: (
          <>
            <p>
              These general Terms and conditions are governed by Dutch law. If a dispute arises from or in
			  connection with the Agreement and/or these General Ticket Terms and Conditionsthat we cannot
              resolve together, it will be submitted to the competent court in Utrecht,Netherlands.
            </p>
          </>
        ),
      },
      {
        title: 'Contact',
        content: (
          <p>
            Questions about these terms? Reach us at{' '}
            <a href="mailto:contact@buydeal.eu">contact@buydeal.eu</a> or write to
            Buydeal.eu B.V., Utrecht, Netherlands.
          </p>
        ),
      },
    ],
    organisers: [
      {
        title: 'About these terms',
        content: (
          <p>
            These terms apply when you register as an event organiser ("Organiser") and
            use the Buydeal platform to create events and sell tickets. By activating your
            account you agree to these terms. They form a binding agreement between you and
            Buydeal.eu B.V., Utrecht, Netherlands.
          </p>
        ),
      },
      {
        title: 'Creating an account',
        content: (
          <>
            <p>
              To sell tickets via Buydeal you must register an organiser account with
              accurate company information including your legal name, Chamber of Commerce
              (KvK) number, VAT number, and IBAN. You are responsible for keeping this
              information up to date.
            </p>
            <p>
              You must be authorised to act on behalf of the organisation you register.
              Buydeal reserves the right to verify your details and reject or suspend
              accounts that do not meet our requirements.
            </p>
          </>
        ),
      },
      {
        title: 'Creating and publishing events',
        content: (
          <>
            <p>
              You are responsible for the accuracy of all event information you publish,
              including the date, time, venue, ticket types, price, and any age or access
              restrictions. Buydeal is not liable for errors in event listings.
            </p>
            <p>
              You must only publish events that you have the legal right to organise and sell
              tickets for. You agree not to list fraudulent, illegal, or misleading events.
              Buydeal may remove any event at our discretion without prior notice if it
              violates these terms or applicable law.
            </p>
          </>
        ),
      },
      {
        title: 'Fees and payouts',
        content: (
          <>
            <p>
              Buydeal charges a service fee or transaction fee per ticket sold. The fee structure is agreed
              separately and shown in your organiser dashboard. Fees may be charged to the
              buyer or absorbed by you  — this needs to be agreed upon before publishing the event.
            </p>
            <p>
              Payouts are processed after the event has taken place, minus any applicable fees
              and any refunds issued. The payout schedule and any holding period will be
              communicated in your organiser agreement. Buydeal uses CM.com  to process
              payments.
            </p>
          </>
        ),
      },
      {
        title: 'Cancellations, changes, and refunds',
        content: (
          <>
            <p>
              If you need to cancel or significantly change your event, you must notify
              Buydeal promptly so we can inform ticket holders. You are responsible for
              communicating with buyers and for your own refund policy.
            </p>
            <p>
              Buydeal will process refunds on your instruction which also includes a refund fee. Refunds issued via the
              platform will be deducted from your payout. You remain liable to buyers for
              any refund obligations under Dutch consumer law even if you do not instruct
              Buydeal to process them.
            </p>
          </>
        ),
      },
      {
        title: 'Your responsibilities',
        content: (
          <>
            <p>As an Organiser you agree to:</p>
            <ul>
              <li>Comply with all applicable Dutch and EU laws, including consumer protection, GDPR, and event regulations</li>
              <li>Hold all required permits, licences, and insurance for your events</li>
              <li>Provide accurate and complete event information</li>
              <li>Respond promptly to buyer support requests escalated by Buydeal</li>
              <li>Not use the platform for any fraudulent, misleading, or unlawful purpose</li>
            </ul>
          </>
        ),
      },
      {
        title: 'Buyer data',
        content: (
          <p>
            Buydeal shares attendee names and email addresses with you so you can manage
            event entry and communicate with your attendees. You are an independent data
            controller for this data and must handle it in accordance with GDPR. You may
            not use attendee data for purposes unrelated to the event without explicit
            consent from attendees.
          </p>
        ),
      },
      {
        title: 'Liability',
        content: (
          <>
            <p>
              You indemnify Buydeal against any claims, losses, or costs arising from
              your events, your event listings, or your breach of these terms. Buydeal is
              not liable for losses caused by event cancellations, changes, or your failure
              to organise an event as advertised.
            </p>
			<>
			<p>
			You are solely responsible for the events you create, promote, and manage through Buydeal. 
			If a third party makes a claim against Buydeal because of your event, event content, actions, or violation 
			of these Terms, you agree to reimburse Buydeal for any resulting losses, damages, legal costs, or expenses.
			</p>

			<p>
			  Buydeal is not responsible for losses arising from event cancellations, postponements, venue changes, 
			  inaccurate event information, low attendance, ticket sales performance, or your failure to deliver an 
			  event as advertised.
			</p>

			<p>
			  To the maximum extent permitted by law, Buydeal's liability is limited to direct damages caused by our 
			  proven negligence or breach of these Terms. Our total liability for any claim will not exceed the service
			   fees paid by you to Buydeal during the three months immediately preceding the event or incident that gave 
			   rise to the claim.
			</p>

			<p>
			  Buydeal is not liable for any indirect, incidental, consequential, or special damages, including loss of 
			  profits, loss of business opportunities, loss of reputation, or loss of data.
			</p>

			</>
          </>
        ),
      },
	  {
	  title: 'Force Majeure',

	  content: (
	  <>

	  Buydeal will not be liable for any failure, delay, interruption, or inability to perform its obligations under these Terms where such failure or delay is caused by circumstances beyond its reasonable control.



	  <p>
	    Such circumstances may include, but are not limited to, natural disasters, severe weather conditions, fire, flood, pandemic, epidemic, government actions or restrictions, war, terrorism, civil unrest, labour disputes, power outages, internet or telecommunications failures, cyberattacks, or failures of third-party service providers.
	  </p>

	  <p>
	    In the event of a force majeure situation, Buydeal may suspend or limit its services for the duration of the affected period without liability. Buydeal will make reasonable efforts to restore services as soon as reasonably practicable.
	  </p>

	  <p>
	    Event organisers remain responsible for managing communications with attendees regarding any event cancellations, postponements, or changes resulting from force majeure circumstances affecting their events.
	  </p>

	  </>
	  )
	  },
      {
        title: 'Account suspension and termination',
        content: (
          <p>
            Buydeal may suspend or close your account at any time if you breach these
            terms, if we suspect fraud, or if your account activity poses a risk to
            buyers. We will give reasonable notice where possible. Ticket sales in progress
            at the time of termination will be handled on a case-by-case basis to protect
            buyers.
          </p>
        ),
      },
      {
        title: 'Applicable law and disputes',
        content: (
          <p>
            These terms are governed by Dutch law. Disputes will be submitted to the
            competent court in Utrecht, Netherlands.
          </p>
        ),
      },
      {
        title: 'Contact',
        content: (
          <p>
            Questions about your organiser account or these terms? Email{' '}
            <a href="mailto:admin@buydeal.eu">admin@buydeal.eu</a>.
          </p>
        ),
      },
    ],
  };

  return (
    <div className="mainhelp-container">

      {/* NAVBAR */}
      <header className="navbar">
        <div className="navbar-inner">
          <div className="logo" onClick={() => navigate("/")}>
            <img src={logo} alt="" />
            <span>Buydeal.EU</span>
          </div>
          <div className="nav-buttons">
            <div className="nav-menu">
              <span className="nav-link" onClick={() => navigate("/orginfo")}>Event Organisers</span>
            </div>
            <div className="nav-menu">
              <span className="nav-link" onClick={() => navigate("/homepagehelp")}>Help</span>
            </div>
            <button className="btn-primary" onClick={() => navigate("/login")}>
              Organiser Login
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="mainhelp">
        <div className="mainhelp-text">
          <h1>Terms &amp; Conditions</h1>
          <p>Buydeal.eu B.V. · Utrecht, Netherlands · Last updated: June 2026</p>
        </div>
      </section>

      <div className="privacy-body">

        <div className="privacy-tabs">
          <button
            className={`privacy-tab${activeTab === 'buyers' ? ' active' : ''}`}
            onClick={() => setActiveTab('buyers')}
          >
            Ticket Buyers
          </button>
          <button
            className={`privacy-tab${activeTab === 'organisers' ? ' active' : ''}`}
            onClick={() => setActiveTab('organisers')}
          >
            Event Organisers
          </button>
        </div>

        <div className="privacy-intro-box">
          {activeTab === 'buyers'
            ? 'These terms cover everything you need to know as a ticket buyer on Buydeal — from placing an order to attending your event. Plain language, no surprises.'
            : 'These terms set out the rules for selling tickets through Buydeal as an event organiser — your obligations, our fees, payouts, and what happens if something changes.'
          }
        </div>

        <div className="privacy-content terms-toc">
          <p className="toc-label">Jump to:</p>
          <div className="toc-links">
            {sections[activeTab].map((s, i) => (
              <a key={i} href={`#tc-${i}`}>{s.title}</a>
            ))}
          </div>
        </div>

        <div className="privacy-content">
          {sections[activeTab].map((s, i) => (
            <div key={i} id={`tc-${i}`} className="privacy-block">
              <h2>{i + 1}. {s.title}</h2>
              {s.content}
            </div>
          ))}
        </div>

      </div>

      {/* FOOTER */}
	  <footer className="privacy-footer">
	  <div className="footer-bottom">
	    <p>
	      © {new Date().getFullYear()} Buydeal.eu B.V. All Rights Reserved.        </p>
	    <div className="footer-legal">
	      <a href="/privacy">Privacy Policy</a>
	      <a href="/toc">Terms &amp; Conditions (Buyers)</a>
	      <a href="/toc?tab=organisers">Terms &amp; Conditions (Organisers)</a>
	    </div>

	  </div>
	  </footer>

    </div>
  );
};

export default TermsAndConditions;
